import React, { useState, useEffect } from 'react';
import { Shield, Brain, Heart, AlertTriangle, Lock, ChevronDown, ChevronUp, Check, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PrimaryButton, SecondaryButton } from './brand/BrandButton';
import { Heading, SmallText } from './brand/BrandTypography';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SignUpDisclaimer Component
 * A comprehensive disclaimer modal for new user registration
 * Content is fetched from the API and can be edited via admin panel
 */

const ICON_MAP = {
    Heart: Heart,
    Brain: Brain,
    Lock: Lock,
    Shield: Shield,
    AlertTriangle: AlertTriangle
};

const DisclaimerSection = ({ title, iconName, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const Icon = ICON_MAP[iconName] || Heart;
    
    return (
        <div className="border border-[#FFBB98]/30 rounded-xl overflow-hidden">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-4 bg-white/40 hover:bg-white/60 transition-colors text-left"
            >
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#1D6B6B]/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[#1D6B6B]" />
                    </div>
                    <span className="font-medium text-[#1D6B6B]">{title}</span>
                </div>
                {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-[#7D8E95]" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-[#7D8E95]" />
                )}
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="p-4 pt-2 text-[#7D8E95] text-sm leading-relaxed space-y-3 bg-white/20">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const ContentBlock = ({ block }) => (
    <>
        {block.heading && (
            <p>
                <strong className="text-[#1D6B6B]">{block.heading}:</strong>{' '}
                {block.text}
            </p>
        )}
        {!block.heading && block.text && <p>{block.text}</p>}
        {block.list && block.list.length > 0 && (
            <ul className="list-disc list-inside space-y-1 ml-2">
                {block.list.map((item, idx) => (
                    <li key={idx}>{item}</li>
                ))}
            </ul>
        )}
    </>
);

const SignUpDisclaimer = ({ isOpen, onAccept, onDecline }) => {
    const [disclaimerContent, setDisclaimerContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [acknowledgments, setAcknowledgments] = useState({});
    
    // Fetch disclaimer content from API
    useEffect(() => {
        if (isOpen && !disclaimerContent) {
            fetchDisclaimer();
        }
    }, [isOpen]);
    
    const fetchDisclaimer = async () => {
        try {
            const response = await fetch(`${API_URL}/api/disclaimer`);
            if (response.ok) {
                const data = await response.json();
                setDisclaimerContent(data);
                // Initialize acknowledgments based on fetched content
                const ackState = {};
                data.acknowledgments?.forEach(ack => {
                    ackState[ack.id] = false;
                });
                setAcknowledgments(ackState);
            }
        } catch (error) {
            console.error('Error fetching disclaimer:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const allAcknowledged = Object.keys(acknowledgments).length > 0 && 
        Object.values(acknowledgments).every(Boolean);
    
    const toggleAcknowledgment = (key) => {
        setAcknowledgments(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-[#1D6B6B]/40 backdrop-blur-sm"
                onClick={onDecline}
            />
            
            {/* Modal */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="relative w-full max-w-2xl max-h-[90vh] bg-[#FEF6EF] rounded-3xl shadow-2xl overflow-hidden"
            >
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-[#1D6B6B]" />
                    </div>
                ) : disclaimerContent ? (
                    <>
                        {/* Header */}
                        <div className="sticky top-0 z-10 bg-gradient-to-b from-[#FEF6EF] to-[#FEF6EF]/95 px-6 py-5 border-b border-[#FFBB98]/30">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-[#1D6B6B]/10 flex items-center justify-center">
                                    <Shield className="w-5 h-5 text-[#1D6B6B]" />
                                </div>
                                <div>
                                    <Heading level={2} className="text-[#1D6B6B]">
                                        {disclaimerContent.title}
                                    </Heading>
                                    <SmallText className="text-[#7D8E95]">
                                        {disclaimerContent.subtitle}
                                    </SmallText>
                                </div>
                            </div>
                        </div>
                        
                        {/* Scrollable Content */}
                        <div 
                            className="overflow-y-auto p-6 space-y-4"
                            style={{ maxHeight: 'calc(90vh - 200px)' }}
                        >
                            {/* Important Notice Banner */}
                            {disclaimerContent.importantNotice && (
                                <div className="bg-[#FFBB98]/20 border border-[#FFBB98]/40 rounded-xl p-4 flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-[#E08850] flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-[#1D6B6B] font-medium text-sm">
                                            {disclaimerContent.importantNotice.title}
                                        </p>
                                        <p className="text-[#7D8E95] text-sm mt-1">
                                            {disclaimerContent.importantNotice.content}
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {/* Dynamic Sections */}
                            {disclaimerContent.sections?.map((section, idx) => (
                                <DisclaimerSection 
                                    key={section.id} 
                                    title={section.title} 
                                    iconName={section.icon}
                                    defaultOpen={idx === 0}
                                >
                                    {section.content?.map((block, blockIdx) => (
                                        <ContentBlock key={blockIdx} block={block} />
                                    ))}
                                </DisclaimerSection>
                            ))}
                            
                            {/* Acknowledgment Checkboxes */}
                            <div className="space-y-3 pt-4 border-t border-[#FFBB98]/30">
                                <SmallText className="text-[#1D6B6B] font-medium">
                                    Please acknowledge the following to continue:
                                </SmallText>
                                
                                {disclaimerContent.acknowledgments?.map((ack) => (
                                    <button 
                                        key={ack.id}
                                        type="button"
                                        onClick={() => toggleAcknowledgment(ack.id)}
                                        className="w-full flex items-start gap-3 p-3 rounded-xl bg-white/40 hover:bg-white/60 cursor-pointer transition-colors text-left"
                                        data-testid={`acknowledge-${ack.id}`}
                                    >
                                        <div 
                                            className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 flex items-center justify-center border-2 transition-colors ${
                                                acknowledgments[ack.id] 
                                                    ? 'bg-[#1D6B6B] border-[#1D6B6B]' 
                                                    : 'border-[#7D8E95]/50 bg-white'
                                            }`}
                                        >
                                            {acknowledgments[ack.id] && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                        <span className="text-sm text-[#3A3A37]">
                                            {ack.text}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Footer Actions */}
                        <div className="sticky bottom-0 bg-gradient-to-t from-[#FEF6EF] to-[#FEF6EF]/95 px-6 py-4 border-t border-[#FFBB98]/30">
                            <div className="flex flex-col sm:flex-row gap-3">
                                <SecondaryButton 
                                    onClick={onDecline}
                                    className="flex-1"
                                    data-testid="decline-disclaimer-btn"
                                >
                                    {disclaimerContent.buttons?.decline || "I'll Come Back Later"}
                                </SecondaryButton>
                                <PrimaryButton 
                                    onClick={onAccept}
                                    disabled={!allAcknowledged}
                                    className="flex-1"
                                    data-testid="accept-disclaimer-btn"
                                >
                                    {allAcknowledged 
                                        ? (disclaimerContent.buttons?.accept || 'I Understand & Accept')
                                        : (disclaimerContent.buttons?.acceptDisabled || 'Please Acknowledge All')
                                    }
                                </PrimaryButton>
                            </div>
                            {!allAcknowledged && (
                                <SmallText className="text-center mt-2 text-[#7D8E95]">
                                    Please check all boxes above to continue
                                </SmallText>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="p-6 text-center text-[#7D8E95]">
                        Failed to load disclaimer. Please try again.
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default SignUpDisclaimer;
